import fileSaver from "file-saver";
import { DeepReadonly } from "solid-js/store";
import { GAME_COLS, GAME_ROWS } from "./constants";
import { BoxState, GameData, GameMode, ShareType } from "./types";
import { useGamesDataContext } from "./GameDataProvider";
import { gtagWrap } from "./utils";

export const getNumEmoji = (num: number) => {
  if (num < 0) {
    return "\uD83D\uDFE5";
  } else if (num === 0) {
    return "1️⃣";
  } else if (num === 1) {
    return "2️⃣";
  } else if (num === 2) {
    return "3️⃣";
  } else if (num === 3) {
    return "4️⃣";
  } else if (num === 4) {
    return "5️⃣";
  } else if (num === 5) {
    return "6️⃣";
  } else if (num === 6) {
    return "7️⃣";
  } else if (num === 7) {
    return "8️⃣";
  } else if (num === 8) {
    return "9️⃣";
  }
};

const getEmojiRow = (states: readonly BoxState[] | undefined) => {
  let emojis = "";
  if (!states || states.length === 0) {
    return "\u2B1B\u2B1B\u2B1B\u2B1B\u2B1B";
  }
  for (let i = 0; i < states.length; i++) {
    if (states[i] === "correct") {
      emojis += "\uD83D\uDFE9";
    } else if (states[i] === "diff") {
      emojis += "\uD83D\uDFE8";
    } else if (states[i] === "none") {
      emojis += "\u2B1C";
    }
  }
  return emojis;
};

const getColorFromState = (state: BoxState | undefined) => {
  if (!state) return "#2d2d2d";
  if (state === "correct") return "#00cc88";
  if (state === "diff") return "#ffcc00";
  if (state === "none") return "#e0e0e0";
  return "#2d2d2d";
};

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const radius_obj = { tl: radius, tr: radius, br: radius, bl: radius };
  ctx.beginPath();
  ctx.moveTo(x + radius_obj.tl, y);
  ctx.lineTo(x + width - radius_obj.tr, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius_obj.tr);
  ctx.lineTo(x + width, y + height - radius_obj.br);
  ctx.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius_obj.br,
    y + height
  );
  ctx.lineTo(x + radius_obj.bl, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius_obj.bl);
  ctx.lineTo(x, y + radius_obj.tl);
  ctx.quadraticCurveTo(x, y, x + radius_obj.tl, y);
  ctx.closePath();
  ctx.fill();
}

export const getShareText = (
  mode: GameMode,
  gameData: DeepReadonly<GameData>,
  sharingMode: boolean
): [string, string] => {
  let textShare = "";
  if ((mode === "daily")||(mode === "archives")) {
    textShare =
      "@QuordleFr Jour #" +
      gameData.seed.toString(10) +
      "\n" +
      getNumEmoji(gameData.answersCorrect[0]) +
      getNumEmoji(gameData.answersCorrect[1]) +
      "\n" +
      getNumEmoji(gameData.answersCorrect[2]) +
      getNumEmoji(gameData.answersCorrect[3]);
  } else {
    textShare =
      "@QuordleFr Entrainement\n" +
      getNumEmoji(gameData.answersCorrect[0]) +  getNumEmoji(gameData.answersCorrect[1]) + " (" + gameData.answers[0].toUpperCase() + " - " + gameData.answers[1].toUpperCase() + ")\n" + getNumEmoji(gameData.answersCorrect[2]) + getNumEmoji(gameData.answersCorrect[3]) + " (" + gameData.answers[2].toUpperCase() + " - " + gameData.answers[3].toUpperCase() + ")";
  }

  textShare += "\nquordle.desmaths.fr";
  const textMobileShare = textShare;
  console.log(sharingMode);
  if(sharingMode){
    textShare += "\n";
    let l1 = GAME_ROWS - 1;
    if (gameData.answersCorrect[0] >= 0 && gameData.answersCorrect[1] >= 0) {
      l1 = Math.max(gameData.answersCorrect[0], gameData.answersCorrect[1]);
    }
    let l2 = GAME_ROWS - 1;
    if (gameData.answersCorrect[2] >= 0 && gameData.answersCorrect[3] >= 0) {
      l2 = Math.max(gameData.answersCorrect[2], gameData.answersCorrect[3]);
    }
    for (let i = 0; i <= l1; i++) {
      textShare +=
        getEmojiRow(gameData.states[0][i]) +
        " " +
        getEmojiRow(gameData.states[1][i]) +
        "\n";
    }
    textShare += "\n";
    for (let i = 0; i <= l2; i++) {
      textShare +=
        getEmojiRow(gameData.states[2][i]) +
        " " +
        getEmojiRow(gameData.states[3][i]) +
        "\n";
    }
  }
  return [textShare, textMobileShare];
};

export const shareGame = async (
  mode: GameMode,
  gameData: DeepReadonly<GameData>,
  shareType: ShareType,
  sharingMode: boolean
) => {
  const [textShare, textMobileShare] = getShareText(mode, gameData, sharingMode);

  gtagWrap("event", "share", {
    mode: mode,
    share_type: shareType,
    daily_seed: mode === "daily" ? gameData.seed : undefined,
  });

  if (shareType === "clipboard") {
    // Deprecated in favor of document.execCommand("copy") method
    navigator.clipboard
      .writeText(textShare)
      .then(() => alert("Résultats copiés dans le presse-papier !"))
      .catch((e) => {
        console.error(e);
        alert("Erreur lors de la copie dans le presse-papier !");
      });
  } else if (shareType === "share") {
    navigator
      .share({
        text: textShare,
      })
      .catch((e) => console.error(e));
  } else if (shareType === "image" || shareType === "image_save") {
    const canvas = document.createElement("canvas");
    canvas.style.display = "none";

    let l1 = GAME_ROWS - 1;
    if (gameData.answersCorrect[0] >= 0 && gameData.answersCorrect[1] >= 0) {
      l1 = Math.max(gameData.answersCorrect[0], gameData.answersCorrect[1]);
    }
    let l2 = GAME_ROWS - 1;
    if (gameData.answersCorrect[2] >= 0 && gameData.answersCorrect[3] >= 0) {
      l2 = Math.max(gameData.answersCorrect[2], gameData.answersCorrect[3]);
    }

    const size = 64;
    const padding = size / 16;
    const radius = size / 8;
    const fontScale = 0.75;
    const textPadding = size / 4;
    canvas.width = (size + padding) * 11 - padding;
    canvas.height = (size + padding) * (l1 + 1 + l2 + 1 + 4) - padding;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // draw words 1 + 2
    let x = 0;
    let y = 0;
    for (let i = 0; i <= l1; i++) {
      let states = gameData.states[0][i];
      for (x = 0; x < GAME_COLS; x++) {
        ctx.fillStyle = getColorFromState(states?.[x]);
        roundRect(
          ctx,
          x * (size + padding),
          y * (size + padding),
          size,
          size,
          radius
        );
      }

      states = gameData.states[1][i];
      for (x = 6; x < GAME_COLS + 6; x++) {
        ctx.fillStyle = getColorFromState(states?.[x - 6]);
        roundRect(
          ctx,
          x * (size + padding),
          y * (size + padding),
          size,
          size,
          radius
        );
      }
      y++;
    }

    // draw middle text
    ctx.font = size * fontScale + "px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#ffffff";

    const titleText =
      mode === "free"
        ? "@QuordleFr Entrainement"
        : "@QuordleFr Jour #" + gameData.seed.toString(10);
    let metrics = ctx.measureText(titleText);
    let fontHeight = metrics.actualBoundingBoxAscent;
    ctx.fillText(
      titleText,
      canvas.width / 2,
      y * (size + padding) + size - (size - fontHeight) / 2,
      canvas.width - textPadding * 2
    );
    y++;
    for (let row = 0; row < 2; row++) {
      for (let i = 0; i < 2; i++) {
        ctx.fillStyle =
          gameData.answersCorrect[i + row * 2] >= 0 ? "#00a6ed" : "#f8312f";
        const dir = i * 2 - 1;
        const xTopCenter =
          canvas.width / 2 + dir * (padding / 2) + dir * (size / 2);
        roundRect(
          ctx,
          xTopCenter - size / 2,
          y * (size + padding),
          size,
          size,
          radius
        );
        if (gameData.answersCorrect[i + row * 2] >= 0) {
          ctx.textAlign = "center";
          ctx.fillStyle = "#ffffff";
          const numText = String(gameData.answersCorrect[i + row * 2] + 1);
          metrics = ctx.measureText(numText);
          fontHeight =
            metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
          ctx.fillText(
            numText,
            xTopCenter,
            y * (size + padding) + size - (size - fontHeight) / 2,
            size
          );
        }
      }
      if (mode === "free") {
        ctx.textAlign = "right";
        ctx.fillStyle = "#ffffff";
        let wordText = gameData.answers[0 + row * 2].toUpperCase();
        metrics = ctx.measureText(wordText);
        fontHeight =
          metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
        ctx.fillText(
          wordText,
          canvas.width / 2 - padding / 2 - size - textPadding,
          y * (size + padding) + size - (size - fontHeight) / 2,
          canvas.width / 2 - padding - size - textPadding * 2
        );
        ctx.textAlign = "left";
        wordText = gameData.answers[1 + row * 2].toUpperCase();
        metrics = ctx.measureText(wordText);
        fontHeight =
          metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
        ctx.fillText(
          wordText,
          canvas.width / 2 + padding / 2 + size + textPadding,
          y * (size + padding) + size - (size - fontHeight) / 2,
          canvas.width / 2 - padding - size - textPadding * 2
        );
      }
      y++;
    }
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(
      "quordle.desmaths.fr",
      canvas.width / 2,
      y * (size + padding) + size / 2,
      canvas.width
    );
    y++;

    // draw words 3 + 4
    for (let i = 0; i <= l2; i++) {
      x = 0;
      let states = gameData.states[2][i];
      for (x = 0; x < GAME_COLS; x++) {
        ctx.fillStyle = getColorFromState(states?.[x]);
        roundRect(
          ctx,
          x * (size + padding),
          y * (size + padding),
          size,
          size,
          radius
        );
      }

      states = gameData.states[3][i];
      for (x = 6; x < GAME_COLS + 6; x++) {
        ctx.fillStyle = getColorFromState(states?.[x - 6]);
        roundRect(
          ctx,
          x * (size + padding),
          y * (size + padding),
          size,
          size,
          radius
        );
      }
      y++;
    }

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob(resolve)
    );

    if (!blob) return;
    const filename = `quordle-${mode === "free" ? "entrainement" : "jour"}-${
      gameData.seed
    }.png`;
    const file = new File([blob], filename, { type: "image/png" });

    if (shareType === "image") {
      navigator
        .share({
          files: [file],
          text: textMobileShare,
        })
        .catch((e) => console.error(e));
    } else if (shareType === "image_save") {
      fileSaver.saveAs(file, filename);
    }
  }
};
